import FuzzySearch from 'fuzzy-search';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { bibleStudySlice } from '../../../model/bibleStudySlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { reorder } from '../../../util/reorder';
import { useRequest } from '../../../util/use-request';
import { RhTab } from '../../core/RhTabs';
import { BibleStudiesScreen } from '../components/BibleStudiesScreen';

export type BibleStudiesProps = {
  // Inputs
  /** current tab id on the uri */
  currentTabId: string;
  /** tabs of this view */
  tabs: RhTab[];
  bibleStudies: BibleStudy[] | null;
  isLoading: boolean;
  isMyStudiesOnly: boolean;
  isEditingOrder: boolean;
  searchTerm: string;
  // Inputs
  userClickedEditOrder: () => void;
  /** called when the user clicks a tab */
  userSelectedTab: (id: string) => void;
  /** navigates to add bible study route */
  userClickedAdd: () => void;
  /** navigates to view single bible study route */
  userClickedAStudy: (id: string) => void;
  /** called when the user updates the search field */
  userUpdatedSearchTerm: (searchTerm: string) => void;
  userClickedMyStudies: () => void;
  userToggledFeatureStatus: (id: string, payload: { isFeatured: boolean }) => void;
  userEndedDrag: (result: DropResult) => void;
  userClickedSaveOrder: () => void;
  userCanceledReorder: () => void;

  currentPage: number;
  hasNextPage: boolean;
  userClickedPrevPage: () => void;
  userClickedNextPage: () => void;
  showPagination: boolean;
};

export const BibleStudiesContainer = () => {
  const bibleStudies = useSelector((state: RootState) => state.bibleStudy.list);

  const [currentTabId, setCurrentTabId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [isGettingPrograms, setIsGettingPrograms] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [isMyStudiesOnly, setIsMyStudiesOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const PAGE_SIZE = 5;
  const SEARCH_DEBOUNCE_MS = 400;
  const searchTimeoutRef = useRef<number | null>(null);

  const request = useRequest();
  const history = useHistory();
  const dispatch = useDispatch();
  const sortFeaturedByOrder = useCallback((studies: BibleStudy[]) => {
    return [...studies].sort((a, b) => {
      const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }, []);
  const getStudyFilters = useCallback(
    (tabId = currentTabId, myStudiesOnly = isMyStudiesOnly) => {
      const params: { isFeatured?: boolean; showOnlyFromCurrentUser?: boolean } = {};
      if (tabId === 'featured-studies') params.isFeatured = true;
      if (myStudiesOnly) params.showOnlyFromCurrentUser = true;
      return Object.keys(params).length ? params : undefined;
    },
    [currentTabId, isMyStudiesOnly]
  );

  const loadBibleStudies = useCallback(
    async (params?: { isFeatured?: boolean; showOnlyFromCurrentUser?: boolean; name?: string }, page = 1) => {
      try {
        setIsGettingPrograms(true);
        const offset = (page - 1) * PAGE_SIZE;

        const bibleStudies = await request<BibleStudy[]>(
          apiConfigs.getBibleStudies({
            ...(params || {}),
            limit: PAGE_SIZE,
            offset,
          })
        );
        const studiesToDisplay = params?.isFeatured ? sortFeaturedByOrder(bibleStudies) : bibleStudies;
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies: studiesToDisplay }));
        setHasNextPage(bibleStudies.length === PAGE_SIZE);
        return studiesToDisplay;
      } catch (err: any) {
        toast.error(err.message || 'Failed to load studies!');
        setHasNextPage(false);
        return [];
      } finally {
        setIsGettingPrograms(false);
      }
    },
    [dispatch, request, sortFeaturedByOrder]
  );

  useEffect(() => {
    loadBibleStudies(getStudyFilters('all', false), 1);
  }, []);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const searcher = new FuzzySearch(bibleStudies || [], [
    'publishingStatus',
    'description',
    'dateCreated',
    'authorName',
    'title',
  ]);

  return (
    <BibleStudiesScreen
      tabs={[
        { id: 'all', title: 'All' },
        { id: 'featured-studies', title: 'Featured Studies' },
      ]}
      currentTabId={currentTabId}
      bibleStudies={searcher.search(searchTerm)}
      isLoading={isGettingPrograms || isUpdating}
      isMyStudiesOnly={isMyStudiesOnly}
      isEditingOrder={isEditingOrder}
      searchTerm={searchTerm}
      userClickedEditOrder={async () => {
        setSearchTerm('');
        try {
          setIsGettingPrograms(true);
          const params = getStudyFilters('featured-studies');
          const allFeatured = await request<BibleStudy[]>(apiConfigs.getBibleStudies(params));
          dispatch(bibleStudySlice.actions.receivedList({ bibleStudies: sortFeaturedByOrder(allFeatured) }));
          setIsEditingOrder(true);
        } catch (err: any) {
          toast.error(err.message || 'Failed to load studies for reordering');
        } finally {
          setIsGettingPrograms(false);
        }
      }}
      userUpdatedSearchTerm={term => {
        setSearchTerm(term);
        setCurrentPage(1);
        if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = window.setTimeout(() => {
          const base = getStudyFilters(currentTabId, isMyStudiesOnly) || {};
          loadBibleStudies({ ...base, ...(term.trim() ? { name: term.trim() } : {}) }, 1);
        }, SEARCH_DEBOUNCE_MS);
      }}
      userClickedAdd={() => {
        history.push(routes.BIBLE_STUDIES__ADD);
      }}
      userSelectedTab={id => {
        if (currentTabId !== id) {
          setCurrentTabId(id);
          setSearchTerm('');
          setCurrentPage(1);
          loadBibleStudies(getStudyFilters(id), 1);
        }
      }}
      userClickedAStudy={id => {
        history.push(routes.BIBLE_STUDIES__VIEW(id));
      }}
      userClickedMyStudies={async () => {
        if (!isMyStudiesOnly) {
          setIsMyStudiesOnly(true);
          setCurrentPage(1);
          const params: { isFeatured?: boolean; showOnlyFromCurrentUser?: boolean } = {
            showOnlyFromCurrentUser: true,
          };
          if (currentTabId === 'featured-studies') params.isFeatured = true;

          const studies = await loadBibleStudies(params, 1);
          if (!studies.length) toast.info("You don't have any studies yet!");
        } else {
          setIsMyStudiesOnly(false);
          setCurrentPage(1);
          loadBibleStudies(getStudyFilters(currentTabId, false), 1);
        }
      }}
      userToggledFeatureStatus={async (id, { isFeatured }) => {
        try {
          setIsUpdating(true);
          const programIndex = bibleStudies.findIndex(study => study.id === id);
          if (programIndex === -1) throw new Error('Program does not exist');

          const program = bibleStudies[programIndex];
          const payload = { ...program, isFeatured };
          await request(apiConfigs.putBibleStudy(payload));

          loadBibleStudies(getStudyFilters(), currentPage);

          if (isFeatured) {
            toast.success(`${program.title} added to Featured!`);
          } else {
            toast.success(`${program.title} removed from Featured!`);
          }
        } catch (e: any) {
          toast.error(e.message || 'Failed to add to featured');
        } finally {
          setIsUpdating(false);
        }
      }}
      userEndedDrag={result => {
        // dropped outside the list
        if (!result.destination || !bibleStudies?.length) {
          return;
        }
        const newBibleStudies = reorder(bibleStudies, result.source.index, result.destination.index);
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies: newBibleStudies }));
      }}
      userClickedSaveOrder={async () => {
        setIsEditingOrder(false);
        setIsUpdating(true);

        const newFeaturedStudies = [...bibleStudies];
        const requests = newFeaturedStudies.map((study, i) => {
          return request(apiConfigs.putBibleStudy({ ...study, sortOrder: i }));
        });
        const results = await Promise.allSettled(requests);
        const failed = results.some(item => item.status === 'rejected');
        setIsUpdating(false);
        if (failed) {
          toast.error('There are items that were not updated!');
        } else {
          toast.success('Successfully updated featured studies!');
          setCurrentPage(1);
          const reloadParams = getStudyFilters('featured-studies');
          loadBibleStudies(reloadParams, 1);
        }
      }}
      userCanceledReorder={() => {
        setIsEditingOrder(false);
        loadBibleStudies(getStudyFilters(), currentPage);
      }}
      currentPage={currentPage}
      hasNextPage={hasNextPage}
      userClickedPrevPage={() => {
        if (currentPage <= 1) return;
        const nextPage = currentPage - 1;
        setCurrentPage(nextPage);

        loadBibleStudies(getStudyFilters(), nextPage);
      }}
      userClickedNextPage={() => {
        if (!hasNextPage) return;
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        loadBibleStudies(getStudyFilters(), nextPage);
      }}
      showPagination={true}
    />
  );
};
